import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';
import { useEffect, useState } from 'react';
import { hasPermission } from '@/types';
import { useAuth } from '@/auth/AuthContext';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { offers, contracts } from '@/api/endpoints';
import type { Offer, OfferStatus } from '@/types';
import { Icon } from '@/components/Icon';
import { OfferEmailModal } from '@/components/OfferEmailModal';
import { OfferStartWithoutContractModal } from '@/components/OfferStartWithoutContractModal';
import { validateUploadSize } from '@/lib/uploadLimits';
import { UploadProgressBar } from '@/components/UploadProgress';
import { OfferDocumentView } from '@/components/OfferDocumentView';
import { OfferAttachmentsPanel } from '@/components/OfferAttachmentsPanel';
import { formatMoney } from '@/lib/money';

const STATUS_COLOR: Record<OfferStatus, string> = {
  DRAFT: 'bg-ink-100 text-ink-600',
  SENT: 'bg-water-100 text-water-700',
  ACCEPTED: 'bg-green-100 text-green-700',
  ORDERED: 'bg-emerald-100 text-emerald-700',
  REJECTED: 'bg-red-100 text-red-700',
  EXPIRED: 'bg-amber-100 text-amber-700',
  CONVERTED: 'bg-purple-100 text-purple-700',
};

const ACTIONS: Record<OfferStatus, { to: OfferStatus; label: string; variant: 'primary' | 'secondary' | 'danger' }[]> = {
  DRAFT: [{ to: 'SENT', label: 'Oznacz jako wysłaną', variant: 'primary' }],
  SENT: [
    { to: 'ACCEPTED', label: 'Zaakceptowana', variant: 'primary' },
    { to: 'REJECTED', label: 'Odrzucona', variant: 'danger' },
    { to: 'EXPIRED', label: 'Wygasła', variant: 'secondary' },
  ],
  ACCEPTED: [],
  ORDERED: [],
  REJECTED: [],
  EXPIRED: [],
  CONVERTED: [],
};

export function OfferDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [offer, setOffer] = useState<Offer | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const { user } = useAuth();
  // Parytet z OfferApi: wysyłka to OFFER_SEND (jedyna akcja wychodząca poza firmę),
  // zmiana statusu to OFFER_EDIT.
  const mozeWyslac = hasPermission(user, 'OFFER_SEND');
  const mozeEdytowac = hasPermission(user, 'OFFER_EDIT');
  const [startB_open, setStartBOpen] = useState(false);
  const [markOrderedOpen, setMarkOrderedOpen] = useState(false);
  // Pliki z kreatora, których nie udało się wgrać po zapisaniu oferty (przekazane w stanie nawigacji).
  const location = useLocation();
  const attachmentErrors = (location.state as { attachmentErrors?: string[] } | null)?.attachmentErrors ?? [];

  const load = () => {
    setOffer(null); setErr(null);
    offers.get(Number(id)).then(setOffer).catch(e => setErr(e?.message ?? String(e)));
  };

  useEffect(load, [id]);

  const changeStatus = (next: OfferStatus) => {
    if (!offer) return;
    setBusy(true); setErr(null);
    offers.changeStatus(offer.id, next)
      .then(setOffer)
      .catch(e => setErr(e?.message ?? String(e)))
      .finally(() => setBusy(false));
  };

  const onZarejestrujZlecenie = async () => {
    if (!offer) return;
    setBusy(true); setErr(null);
    try {
      // Backend: znajduje istniejące zlecenie dla ujęcia oferty albo automatycznie je tworzy,
      // następnie generuje draft umowy AI z kontekstem RAG. Po sukcesie idziemy do edytora.
      const contract = await contracts.aiGenerateFromOffer(offer.id);
      nav(`/umowa/${contract.id}`);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  if (err) return <div className="text-red-500">Błąd: {err}</div>;
  if (!offer) return <div className="text-ink-400">Ładowanie…</div>;

  // Oferta edytowalna: DRAFT zawsze, ACCEPTED tylko gdy nie ma jeszcze umowy.
  const isEditable = offer.status === 'DRAFT'
    || (offer.status === 'ACCEPTED' && !offer.convertedContractId);
  const missingIntake = !offer.intakeId;
  const actions = ACTIONS[offer.status];

  return (
    <div className="max-w-[1200px]">
      <header className="flex items-end justify-between mb-5 flex-wrap gap-3">
        <div>
          <div className="text-[12px] text-ink-400 font-mono">{offer.number} · {offer.typeName}</div>
          <h1>{offer.title}</h1>
          <div className="flex items-center gap-2 mt-1">
            <span className={'inline-block px-2 py-0.5 rounded text-[11px] font-semibold ' + STATUS_COLOR[offer.status]}>
              {offer.statusName}
            </span>
            {offer.company && <span className="text-[12px] text-ink-500">Firma: <strong>{offer.company.shortName}</strong></span>}
            {offer.intakes && offer.intakes.length > 1
              ? <span className="text-[12px] text-ink-500">Ujęcia: {offer.intakes.map(u => u.name).join(', ')}</span>
              : offer.intakeName && <span className="text-[12px] text-ink-500">Ujęcie: {offer.intakeName}</span>}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => nav('/oferty')} className="btn btn-secondary btn-sm">
            <Icon name="chevron" className="w-3.5 h-3.5 rotate-180"/> Powrót
          </button>
          {isEditable && (
            <button onClick={() => nav(`/oferta-wizard/${offer.id}`)} className="btn btn-secondary btn-sm">
              <Icon name="settings" className="w-3.5 h-3.5"/> Edytuj
            </button>
          )}
          <a href={`/api/offers/${offer.id}/pdf`} download className="btn btn-secondary btn-sm">
            <Icon name="file-text" className="w-3.5 h-3.5"/> PDF
          </a>
          <a href={`/api/offers/${offer.id}/docx`} download className="btn btn-secondary btn-sm">
            <Icon name="file-text" className="w-3.5 h-3.5"/> DOCX
          </a>
          {mozeWyslac && (
            <button onClick={() => setEmailModalOpen(true)} className="btn btn-primary btn-sm">
              <Icon name="bell" className="w-3.5 h-3.5"/> Wyślij e-mail
            </button>
          )}
          {mozeEdytowac && actions.map(a => (
            <button key={a.to} disabled={busy} onClick={() => changeStatus(a.to)}
              className={'btn btn-sm ' + (a.variant === 'primary' ? 'btn-primary' : a.variant === 'danger' ? 'btn-secondary text-red-600' : 'btn-secondary')}>
              {a.label}
            </button>
          ))}
          {offer.status === 'ACCEPTED' && offer.kindFollowUp !== 'ORDER'
            && !offer.convertedContractId && !offer.convertedStudyId && (
            <>
              <button onClick={onZarejestrujZlecenie}
                      disabled={busy || missingIntake}
                      title={missingIntake
                        ? 'Oferta nie ma przypisanego ujęcia — konwersja wymaga ujęcia. Kliknij Edytuj, aby je uzupełnić.'
                        : 'Ścieżka A: AI wygeneruje draft naszej umowy z oferty i RAG-em z archiwum'}
                      className="btn btn-primary btn-sm">
                <Icon name="sparkles" className="w-3.5 h-3.5"/>
                {busy ? 'Rejestruję zlecenie…' : 'Wygeneruj umowę AI i rozpocznij'}
              </button>
              <button onClick={() => setStartBOpen(true)}
                      disabled={busy || missingIntake}
                      title={missingIntake
                        ? 'Oferta nie ma przypisanego ujęcia — wymagane.'
                        : 'Ścieżka B: rozpocznij jako zwykłe zlecenie (bez umowy). Plik zlecenia od klienta opcjonalny.'}
                      className="btn btn-secondary btn-sm">
                <Icon name="file-text" className="w-3.5 h-3.5"/>
                Rozpocznij jako zwykłe zlecenie
              </button>
            </>
          )}
          {(offer.status === 'SENT' || offer.status === 'ACCEPTED')
            && offer.kindFollowUp === 'ORDER'
            && !offer.convertedContractId && !offer.convertedStudyId && (
            <button onClick={() => setMarkOrderedOpen(true)} disabled={busy}
                    title="Oznacz ofertę jako zlecenie (bez umowy/opracowania). Można dołączyć dokument zlecenia klienta."
                    className="btn btn-primary btn-sm">
              <Icon name="check" className="w-3.5 h-3.5"/> Oznacz jako zlecenie
            </button>
          )}
          {offer.convertedContractId && (
            <button onClick={() => nav(`/umowa/${offer.convertedContractId}`)} className="btn btn-secondary btn-sm">
              <Icon name="file-text" className="w-3.5 h-3.5"/> Otwórz draft umowy (#{offer.convertedContractId})
            </button>
          )}
          {offer.convertedStudyId && (
            <button onClick={() => nav(`/studies/${offer.convertedStudyId}`)} className="btn btn-secondary btn-sm">
              <Icon name="file-text" className="w-3.5 h-3.5"/> Otwórz opracowanie (#{offer.convertedStudyId})
            </button>
          )}
        </div>
      </header>

      {offer.status === 'ACCEPTED' && missingIntake && !offer.convertedContractId && (
        <div className="mb-4 p-3 rounded-md border border-amber-200 bg-amber-50 flex items-start gap-3">
          <Icon name="bell" className="w-4 h-4 text-amber-700 mt-0.5 shrink-0"/>
          <div className="flex-1 text-[13px]">
            <div className="font-semibold text-amber-900">Brak przypisanego ujęcia</div>
            <div className="text-amber-800 mt-0.5">
              Konwersja na umowę i zarejestrowanie zlecenia wymaga ujęcia (każde zlecenie jest powiązane
              z konkretnym ujęciem klienta). Otwórz ofertę w trybie edycji i wybierz ujęcie.
            </div>
          </div>
          <button onClick={() => nav(`/oferta-wizard/${offer.id}`)} className="btn btn-primary btn-sm shrink-0">
            <Icon name="settings" className="w-3.5 h-3.5"/> Edytuj ofertę
          </button>
        </div>
      )}

      <div className="grid grid-cols-[2fr_1fr] gap-5">
        <div className="flex flex-col gap-4">

          {/* Podgląd pisma — bloki przysłane przez serwer, te same co w PDF i DOCX (V160). */}
          <div className="card">
            <div className="card-head">
              <div className="card-title">Treść oferty (podgląd)</div>
              <div className="text-[11px] text-ink-400 ml-auto">dokładnie to trafi do PDF i DOCX</div>
            </div>
            <div className="card-body bg-ink-50 p-6">
              {offer.documentView
                ? <OfferDocumentView doc={offer.documentView}/>
                : <div className="text-[13px] text-ink-400">Ładowanie podglądu…</div>}
            </div>
          </div>

          {offer.type === 'LINE_ITEMS' && offer.lineItems && offer.lineItems.length > 0 && (
            <div className="card">
              <div className="card-head">
                <div className="card-title">Pozycje kosztorysu</div>
                <div className="text-[11px] text-ink-400 ml-auto">terminy pozycji nie są drukowane na piśmie</div>
              </div>
              <div className="card-body !p-0">
                <table className="table-hw">
                  <thead>
                    <tr>
                      <th className="w-10">#</th>
                      <th className="w-40">Grupa</th>
                      <th>Opis</th>
                      <th className="w-24">Jedn.</th>
                      <th className="w-24 text-right">Ilość</th>
                      <th className="w-36">Termin</th>
                      <th className="w-32 text-right">Cena netto</th>
                      <th className="w-32 text-right">Wartość netto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {offer.lineItems.map(li => (
                      <tr key={li.id ?? li.position}>
                        <td className="font-mono text-[12px] text-ink-500">{li.position}</td>
                        {/* Grupa decyduje o podsumach na piśmie — bez tej kolumny karta
                            oferty nie tłumaczy, skąd w dokumencie wzięły się nagłówki. */}
                        <td className="text-[12px] text-ink-700">{li.groupLabel ?? '—'}</td>
                        <td>{li.description}</td>
                        <td className="font-mono text-[12px]">{li.unit}</td>
                        <td className="text-right font-mono text-[12px]">{li.quantity}</td>
                        {/* Termin wpisany w kreatorze był dotąd niewidoczny wszędzie poza samym
                            kreatorem, mimo że serwer liczy gotową etykietę (V160). */}
                        <td className="text-[12px] text-ink-700">{li.deadlineLabel ?? '—'}</td>
                        <td className="text-right font-mono text-[12px]">{formatMoney(li.unitPriceNet)} zł</td>
                        <td className="text-right font-mono text-[12px] text-ink-800">{formatMoney(li.lineTotalNet ?? 0)} zł</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {offer.notes && (
            <div className="card">
              <div className="card-head"><div className="card-title">Uwagi wewnętrzne</div></div>
              <div className="card-body whitespace-pre-wrap text-[13px] text-ink-700">{offer.notes}</div>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-4">
          <div className="card">
            <div className="card-head"><div className="card-title">Klient</div></div>
            <div className="card-body">
              <div className="font-semibold text-ink-900">
                {offer.client.name}
                {offer.client.city && <span className="font-normal text-ink-500"> · {offer.client.city}</span>}
              </div>
              {offer.client.nip && <div className="text-[12px] text-ink-500">NIP: {offer.client.nip}</div>}
              {(offer.client.addressLine1 || offer.client.city) && (
                <div className="text-[12px] text-ink-500">
                  {offer.client.addressLine1 && <div>{offer.client.addressLine1}</div>}
                  <div>{[offer.client.postalCode, offer.client.city].filter(Boolean).join(' ')}</div>
                </div>
              )}
              {offer.client.email && <div className="text-[12px] text-ink-500 mt-1">{offer.client.email}</div>}
              {offer.client.phone && <div className="text-[12px] text-ink-500 font-mono">{offer.client.phone}</div>}
            </div>
          </div>

          <div className="card">
            <div className="card-head"><div className="card-title">Kwoty</div></div>
            <div className="card-body flex flex-col gap-2 text-[13px]">
              {offer.type === 'LUMP_SUM' && offer.unitPrice != null && (
                <div className="flex flex-col gap-1 text-[12px] text-ink-500 mb-1">
                  <div className="flex justify-between gap-3">
                    <span>Ryczałt{offer.unitLabel ? ` za ${offer.unitLabel}` : ''}</span>
                    <span className="font-mono tabular-nums whitespace-nowrap">
                      {formatMoney(offer.unitPrice)} zł{(offer.unitCount ?? 1) > 1 ? ` × ${offer.unitCount}` : ''}
                    </span>
                  </div>
                  {/* Pozycje ad-hoc doliczone do ryczałtu — na piśmie stoją pod ceną ryczałtową. */}
                  {offer.lineItems.map(li => (
                    <div key={li.id ?? li.position} className="flex justify-between gap-3">
                      <span>{li.description}</span>
                      <span className="font-mono tabular-nums whitespace-nowrap">{formatMoney(li.lineTotalNet ?? 0)} zł</span>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex justify-between"><span className="text-ink-500">Netto</span><span className="font-mono tabular-nums text-ink-800">{formatMoney(offer.totalNet)} zł</span></div>
              <div className="flex justify-between"><span className="text-ink-500">VAT ({offer.vatRate}%)</span><span className="font-mono tabular-nums text-ink-800">{formatMoney(offer.totalVat)} zł</span></div>
              <hr className="border-0 border-t border-ink-100 my-1"/>
              <div className="flex justify-between font-semibold text-[14px]"><span className="text-ink-900">Brutto</span><span className="font-mono tabular-nums text-ink-900">{formatMoney(offer.totalGross)} zł</span></div>
              {/* Ta karta to widok WEWNĘTRZNY — na piśmie kwoty mogą być węższe (V160). */}
              <div className="text-[11px] text-ink-400 mt-1">
                Na dokumencie: {offer.showGross ? 'netto, VAT i brutto' : 'tylko netto'}
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-head"><div className="card-title">Daty</div></div>
            <div className="card-body text-[12px] text-ink-700 flex flex-col gap-1">
              <div><span className="text-ink-400">Wystawiona:</span> <span className="font-mono text-ink-800">{offer.issueDate}</span></div>
              <div><span className="text-ink-400">Ważna do:</span> <span className="font-mono text-ink-800">{offer.validUntil ?? '—'}</span></div>
              {offer.sentAt && <div><span className="text-ink-400">Wysłana:</span> <span className="font-mono text-ink-800">{offer.sentAt.slice(0, 16).replace('T', ' ')}</span></div>}
              {offer.decidedAt && <div><span className="text-ink-400">Decyzja:</span> <span className="font-mono text-ink-800">{offer.decidedAt.slice(0, 16).replace('T', ' ')}</span></div>}
              {offer.deadlineLabel && (
                <div>
                  <span className="text-ink-400">Termin realizacji:</span>{' '}
                  <span className="text-ink-800">{offer.deadlineLabel}</span>
                  {!offer.printDeadline && <span className="text-ink-400"> (niedrukowany)</span>}
                </div>
              )}
              {offer.paymentTerms && <div className="mt-1"><span className="text-ink-400">Płatność:</span> {offer.paymentTerms}</div>}
            </div>
          </div>

          {(offer.kindName || offer.boreholeDesignation) && (
            <div className="card">
              <div className="card-head"><div className="card-title">Rodzaj</div></div>
              <div className="card-body text-[12px] text-ink-700 flex flex-col gap-1">
                {offer.kindName && <div><span className="text-ink-400">Rodzaj oferty:</span> <strong>{offer.kindName}</strong></div>}
                {offer.kindFollowUpName && <div><span className="text-ink-400">Po akceptacji:</span> {offer.kindFollowUpName}</div>}
                {offer.boreholeDesignation && <div><span className="text-ink-400">Otwór:</span> <span className="font-mono">{offer.boreholeDesignation}</span></div>}
              </div>
            </div>
          )}

          {attachmentErrors.length > 0 && (
            <div className="p-2 border border-amber-200 bg-amber-50 rounded text-[12px] text-amber-900">
              Oferta zapisana, ale część plików się nie wgrała — dodaj je ponownie poniżej:
              <ul className="list-disc ml-4 mt-1">{attachmentErrors.map(e => <li key={e}>{e}</li>)}</ul>
            </div>
          )}

          <OfferAttachmentsPanel
            offerId={offer.id}
            intakes={offer.intakes && offer.intakes.length > 0
              ? offer.intakes
              : (offer.intakeId ? [{ id: offer.intakeId, name: offer.intakeName ?? `#${offer.intakeId}` }] : [])}
            defaultBoreholeId={offer.boreholeId ?? null}
            canEdit={mozeEdytowac}
            compact/>
        </div>
      </div>

      <OfferEmailModal
        open={emailModalOpen}
        offer={offer}
        onClose={() => setEmailModalOpen(false)}
        onSent={(updated) => { setOffer(updated); setEmailModalOpen(false); }}
      />

      <OfferStartWithoutContractModal
        open={startB_open}
        offer={offer}
        onClose={() => setStartBOpen(false)}
        onStarted={(studyId) => { setStartBOpen(false); nav(`/studies/${studyId}`); }}
      />

      {markOrderedOpen && (
        <MarkOrderedModal
          offer={offer}
          onClose={() => setMarkOrderedOpen(false)}
          onDone={() => { setMarkOrderedOpen(false); load(); }}
        />
      )}
    </div>
  );
}

function MarkOrderedModal({ offer, onClose, onDone }: { offer: Offer; onClose: () => void; onDone: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [orderDate, setOrderDate] = useState(new Date().toISOString().slice(0, 10));
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [uploadPct, setUploadPct] = useState<number | null>(null); // null = brak wysyłki
  const submit = async () => {
    // Blokada rozmiaru: dołączony plik nie może przekraczać 100 MB.
    const sizeErr = validateUploadSize(file);
    if (sizeErr) { setErr(sizeErr); return; }
    setBusy(true); setErr(null); setUploadPct(0);
    try {
      await offers.markOrdered(offer.id, file, { orderDate: orderDate || null, description: description || null },
        p => setUploadPct(p.percent));
      onDone();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      setBusy(false);
    } finally {
      setUploadPct(null);
    }
  };
  const { requestClose, confirmation, dialogProps } = useUnsavedChanges({
    value: { file, orderDate, description }, onClose, busy,
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={requestClose} {...dialogProps}>
      {confirmation}
      <div className="bg-white rounded-lg shadow-2xl max-w-[520px] w-full" onClick={e => e.stopPropagation()}>
        <div className="border-b border-ink-100 px-5 py-3 flex items-center gap-3">
          <h2 className="text-[16px] font-semibold m-0">Oznacz jako zlecenie</h2>
          <button onClick={requestClose} className="ml-auto text-ink-400 hover:text-ink-700"><Icon name="x"/></button>
        </div>
        <div className="p-5 space-y-3">
          {err && <div className="text-red-500 text-[12px]">{err}</div>}
          <div className="text-[12px] text-ink-500">
            Oferta <strong>{offer.number}</strong> zostanie oznaczona jako zlecenie (bez generowania umowy ani opracowania).
            Możesz dołączyć dokument zlecenia od klienta.
          </div>
          <label className="flex flex-col gap-1">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-ink-500">Data zlecenia</span>
            <input type="date" value={orderDate} onChange={e => setOrderDate(e.target.value)} className="input w-44"/>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-ink-500">Dokument zlecenia klienta (opcjonalnie)</span>
            <input type="file" onChange={e => {
              const f = e.target.files?.[0] ?? null;
              // Blokada rozmiaru: dołączony plik nie może przekraczać 100 MB.
              const sizeErr = validateUploadSize(f);
              if (sizeErr) { setErr(sizeErr); return; }
              setErr(null); setFile(f);
            }} className="text-[12px]"/>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-ink-500">Opis (opcjonalnie)</span>
            <input value={description} onChange={e => setDescription(e.target.value)}
                   placeholder="np. Zlecenie e-mailem z 14.05.2026" className="input"/>
          </label>
        </div>
        <div className="border-t border-ink-100 px-5 py-3 flex items-center justify-end gap-2">
          {uploadPct !== null && (
            <UploadProgressBar percent={uploadPct} label="Wysyłanie pliku…" compact className="flex-1"/>
          )}
          <button onClick={requestClose} disabled={busy} className="btn btn-secondary btn-sm">Anuluj</button>
          <button onClick={submit} disabled={busy} className="btn btn-primary btn-sm">
            {busy ? 'Zapisywanie…' : 'Zarejestruj zlecenie'}
          </button>
        </div>
      </div>
    </div>
  );
}
